/*
 * @providesModule AgeVerificationView
 *
 */

import React, {useState, useEffect, useRef} from 'react';
import {
  Text,
  TextInput,
  View,
  TouchableOpacity,
  Linking,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import styles, {createStyles} from 'AgeVerificationStyle';
import TextInputComponent from 'TextInputComponent';
import Toggle from 'react-native-toggle-element';
import Sizes from 'Sizes';
import SkillzSVG from 'SkillzSVG';
import moment from 'moment';
import {_m} from 'INTL';
import {GameThemeBackground} from 'GameThemeBackground';
import SkillzStyle from 'SkillzStyle';
import ClientPropertyConstants from 'ClientPropertyConstants';
import PushButton from 'PushButton';
import SkillzImage from 'SkillzImage';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import {ScrollView} from 'react-native';
import GenericHeader from '../../UnifiedLogin/Components/GenericHeader/GenericHeader';
import {getCohort} from '../../UnifiedLogin/Utility/utils';

export interface AgeVerificationProps {
  //provided minimum age
  minimumAge: number;
  //enable the toggle button
  preCheck?: boolean;
  //Callback on “Close” button
  onClosePress: () => void;
  //Handle callback on "Next" button
  onNextPress: (
    dateToSubmit: string,
    isConsent: boolean,
    onSuccess: () => void,
    onError: () => void
  ) => void;
}

const TEST_IDS = {
  modalTestID: 'age_verification_modal',
  closeTestID: 'age_verification_close',
  titleTestID: 'age_verification_title',
  birthDateHeaderTestID: 'age_verification_birth_date_header',
  birthDateMessageTestID: 'age_verification_birth_date_message',
  stayInLoopHeaderTestID: 'age_verification_stay_in_loop_header',
  stayInLoopMessageTestID: 'age_verification_stay_in_loop_message',
  next: 'age_verification_next',
  serviceTermMessageTestID: 'age_verification_service_terms_message',
  serviceTermButtonTestID: 'age_verification_service_terms_button',
  privatePolicyButtonTestID: 'age_verification_private_policy_button',
  monthInputTestID: 'age_verification_month_input',
  dayInputTestID: 'age_verification_day_input',
  yearInputTestID: 'age_verification_year_input',
  errorMessageTestID: 'age_verification_error_message',
  emailConsentToggleTestID: 'age_verification_email_consent_toggle',
};

const AgeVerificationView = ({preCheck = false, ...props}: AgeVerificationProps) => {
  const [month, setMonth] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [year, setYear] = useState<string>('');
  //True if month/date/year input is focus on for styling
  const [inputMonthFocus, setMonthFocus] = useState<boolean>(false);
  const [inputDateFocus, setDateFocus] = useState<boolean>(false);
  const [inputYearFocus, setYearFocus] = useState<boolean>(false);

  const [isFormatError, setFormatError] = useState<boolean>(false);
  const [isInvalidDate, setInvalidDate] = useState<boolean>(false);
  const [isUnderAge, setUnderAge] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isConsent, setIsConsent] = useState(preCheck);
  const toggleSwitch = () => setIsConsent((previousState) => !previousState);
  const minAge = props.minimumAge ?? 18;

  const {statusBarContentStyle} = SkillzStyle.getOverriddenFallbackTheme();
  const isDarkTheme = statusBarContentStyle === 0;

  const isFTUEv3Enabled = ClientPropertyConstants.isFTUEv3Enabled();

  const style = createStyles(
    isDarkTheme,
    SkillzStyle.getOverriddenFallbackTheme(),
    isFTUEv3Enabled
  );

  const monthRef = useRef<TextInput>(null);
  const dayRef = useRef<TextInput>(null);
  const yearRef = useRef<TextInput>(null);

  useEffect(() => {
    // if (isLoading) return; // Don't auto-submit when loading

    if (inputMonthFocus && month?.length === 2) {
      dayRef?.current?.focus();
      handleNextPress(true); // true = auto-submit
    }
    if (inputDateFocus && date?.length === 2) {
      yearRef?.current?.focus();
      handleNextPress(true); // true = auto-submit
    }
    if (inputYearFocus && year?.length === 4) {
      yearRef?.current?.blur();
      handleNextPress(true); // true = auto-submit
    }
  }, [month, date, year, isLoading]);

  useEffect(() => {
    trackTelemetryEvent({
      event: TelemetryEventName.AGE_INPUT_SCREEN_LODED,
    });
  }, []);

  const resetErrors = () => {
    if (isFormatError || isInvalidDate || isUnderAge) {
      setFormatError(false);
      setInvalidDate(false);
      setUnderAge(false);
    }
  };

  const renderCloseBtn = () => {
    return (
      <TouchableOpacity
        style={styles.closeBtn}
        onPress={props.onClosePress}
        testID={TEST_IDS.closeTestID}
      >
        <SkillzSVG
          source={isFTUEv3Enabled ? (isDarkTheme ? 'close_white.svg' : 'close.svg') : 'close.svg'}
          style={[styles.closeIcons, isDarkTheme && style.closeIconDarkTheme]}
        />
      </TouchableOpacity>
    );
  };

  const renderNavBar = () => {
    return isFTUEv3Enabled ? (
      <GenericHeader
        showRightIcon={false}
        showSkillzLogo={false}
        onLeftIconPress={props.onClosePress}
      />
    ) : (
      <View style={isFTUEv3Enabled ? style.newNavBarContainer : styles.navBarContainer}>
        <Text
          style={isFTUEv3Enabled ? style.newNavBarTitle : styles.navBarTitle}
          testID={TEST_IDS.titleTestID}
          allowFontScaling={false}
        >
          {_m('We need more information')}
        </Text>
        {renderCloseBtn()}
      </View>
    );
  };

  const monthPrompt = () => {
    return (
      <View>
        <TextInputComponent
          testID={TEST_IDS.monthInputTestID}
          accessibilityLabel={TEST_IDS.monthInputTestID}
          value={month}
          style={
            isFormatError || isInvalidDate
              ? styles.inputError
              : inputMonthFocus
                ? isFTUEv3Enabled
                  ? [
                      style.newInputHighlight,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                      style.inputHighlightBorder,
                    ]
                  : styles.inputHighlight
                : isFTUEv3Enabled
                  ? [
                      style.newInput,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                    ]
                  : styles.input
          }
          showIcon={false}
          inputStyle={isFTUEv3Enabled ? style.newInputStyle : styles.inputStyle}
          labelStyle={
            isFormatError || isInvalidDate
              ? styles.labelTextError
              : inputMonthFocus
                ? isFTUEv3Enabled
                  ? style.newLabelHighlightStyle
                  : styles.labelHighlightStyle
                : isFTUEv3Enabled
                  ? style.newLabelStyle
                  : styles.labelStyle
          }
          placeholderStyle={styles.placeholderStyle}
          label={month ? 'Month' : inputMonthFocus ? 'Month' : 'MM'}
          placeholder='MM'
          placeholderTextColor={
            isFTUEv3Enabled
              ? isDarkTheme
                ? SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color
                : SkillzStyle.getOverriddenFallbackTheme().textColorSecondary.color
              : '#757575'
          }
          keyboardType='numeric'
          returnKeyType='done'
          maxLength={2}
          ref={monthRef}
          allowFontScaling={false}
          labelProps={{
            allowFontScaling: false,
          }}
          onChangeText={(value) => {
            setMonth(value);
          }}
          onFocus={() => {
            setMonthFocus(true);
            resetErrors();
          }}
          onBlur={() => setMonthFocus(false)}
        />
      </View>
    );
  };

  const datePrompt = () => {
    return (
      <View>
        <TextInputComponent
          ref={dayRef}
          value={date}
          testID={TEST_IDS.dayInputTestID}
          accessibilityLabel={TEST_IDS.dayInputTestID}
          style={
            isFormatError || isInvalidDate
              ? styles.inputError
              : inputDateFocus
                ? isFTUEv3Enabled
                  ? [
                      style.newInputHighlight,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                      style.inputHighlightBorder,
                    ]
                  : styles.inputHighlight
                : isFTUEv3Enabled
                  ? [
                      style.newInput,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                    ]
                  : styles.input
          }
          showIcon={false}
          inputStyle={isFTUEv3Enabled ? style.newInputStyle : styles.inputStyle}
          labelStyle={
            isFormatError || isInvalidDate
              ? styles.labelTextError
              : inputDateFocus
                ? isFTUEv3Enabled
                  ? style.newLabelHighlightStyle
                  : styles.labelHighlightStyle
                : isFTUEv3Enabled
                  ? style.newLabelStyle
                  : styles.labelStyle
          }
          placeholderStyle={styles.placeholderStyle}
          label={date ? 'Day' : inputDateFocus ? 'Day' : 'DD'}
          placeholder='DD'
          placeholderTextColor={
            isFTUEv3Enabled
              ? isDarkTheme
                ? SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color
                : SkillzStyle.getOverriddenFallbackTheme().textColorSecondary.color
              : '#757575'
          }
          keyboardType='numeric'
          returnKeyType='done'
          maxLength={2}
          allowFontScaling={false}
          labelProps={{
            allowFontScaling: false,
          }}
          onChangeText={(value) => {
            setDate(value);
          }}
          onFocus={() => {
            setDateFocus(true);
            resetErrors();
          }}
          onBlur={() => setDateFocus(false)}
        />
      </View>
    );
  };

  const yearPrompt = () => {
    return (
      <View>
        <TextInputComponent
          ref={yearRef}
          value={year}
          testID={TEST_IDS.yearInputTestID}
          accessibilityLabel={TEST_IDS.yearInputTestID}
          style={
            isFormatError || isInvalidDate
              ? styles.inputError
              : inputYearFocus
                ? isFTUEv3Enabled
                  ? [
                      style.newInputHighlight,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                      style.inputHighlightBorder,
                    ]
                  : styles.inputHighlight
                : isFTUEv3Enabled
                  ? [
                      style.newInput,
                      isDarkTheme ? style.inputBackgroundDark : style.inputBackgroundLight,
                    ]
                  : styles.input
          }
          showIcon={false}
          inputStyle={isFTUEv3Enabled ? style.newInputStyle : styles.inputStyle}
          labelStyle={
            isFormatError || isInvalidDate
              ? styles.labelTextError
              : inputYearFocus
                ? isFTUEv3Enabled
                  ? style.newLabelHighlightStyle
                  : styles.labelHighlightStyle
                : isFTUEv3Enabled
                  ? style.newLabelStyle
                  : styles.labelStyle
          }
          placeholderStyle={styles.placeholderStyle}
          label={year ? 'Year' : inputYearFocus ? 'Year' : 'YYYY'}
          placeholder='YYYY'
          placeholderTextColor={
            isFTUEv3Enabled
              ? isDarkTheme
                ? SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color
                : SkillzStyle.getOverriddenFallbackTheme().textColorSecondary.color
              : '#757575'
          }
          keyboardType='numeric'
          returnKeyType='done'
          maxLength={4}
          allowFontScaling={false}
          labelProps={{
            allowFontScaling: false,
          }}
          onChangeText={(value) => {
            setYear(value);
          }}
          onFocus={() => {
            setYearFocus(true);
            resetErrors();
          }}
          onBlur={() => setYearFocus(false)}
        />
      </View>
    );
  };

  const checkIsUnderAge = (): boolean => {
    const todayYear = new Date().getFullYear();
    const differenceYear = todayYear - Number(year);

    const todayMonth = new Date().getMonth() + 1; //month starts at 0
    const differenceMonth = todayMonth - Number(month);

    const todayDay = new Date().getDate();
    const differenceDay = todayDay - Number(date);

    if (differenceYear < minAge) {
      setUnderAge(true);
      return true;
    } else if (
      differenceYear == minAge &&
      (differenceMonth < 0 || (differenceMonth == 0 && differenceDay < 0))
    ) {
      setUnderAge(true);
      return true;
    } else {
      setUnderAge(false);
      return false;
    }
  };

  const onDateChange = (): boolean => {
    let isFormatError = Boolean(
      (month && month.length < 2) || (date && date.length < 2) || (year && year.length < 4)
    );
    setFormatError(isFormatError);
    if (month && date && year) {
      // check invalid date and is under age
      const validDate =
        moment(`${year}-${month}-${date}`).isValid() && !(Number(year) > new Date().getFullYear());
      isFormatError = isFormatError || !validDate || checkIsUnderAge();
      setInvalidDate(!validDate);
    } else {
      setInvalidDate(false);
      setUnderAge(false);
    }
    //false if no error, true if error
    return isFormatError;
  };

  const errorText = () => {
    if (isFormatError || isInvalidDate) {
      return _m('Format must be MM/DD/YYYY');
    }
    if (isUnderAge) {
      return _m('You are not old enough to access this feature');
    }
    return null;
  };

  const errorMessageView = () => {
    if (errorText()) {
      return (
        <View style={[styles.errorContainer, isFTUEv3Enabled && style.errorContainerMargin]}>
          <SkillzSVG style={styles.errorIcon} source={'info_error.svg'} />
          <Text
            allowFontScaling={false}
            style={styles.errorMessage}
            testID={TEST_IDS.errorMessageTestID}
          >
            {errorText()}
          </Text>
        </View>
      );
    }

    return null;
  };

  const renderDateOfBirth = () => {
    if (isFTUEv3Enabled) {
      return (
        <View style={styles.newDOBContainer}>
          <Text
            allowFontScaling={false}
            style={[style.headerTextCenter, styles.newHeaderText]}
            testID={TEST_IDS.birthDateHeaderTestID}
          >
            {_m('What’s your birthday?')}
          </Text>
          <Text
            allowFontScaling={false}
            style={[style.descriptionTextCenter, styles.newDescriptionText]}
            testID={TEST_IDS.birthDateMessageTestID}
          >
            {_m('Players must be at least 18 years of age to compete on our system')}
          </Text>
          <View style={styles.newRowContainer}>
            {monthPrompt()}
            {datePrompt()}
            {yearPrompt()}
          </View>
          {errorMessageView()}
        </View>
      );
    }
    return (
      <View style={styles.dOBContainer}>
        <Text
          allowFontScaling={false}
          style={styles.headerText}
          testID={TEST_IDS.birthDateHeaderTestID}
        >
          What is your date of birth?
        </Text>
        <Text
          allowFontScaling={false}
          style={[styles.descriptionText, {lineHeight: 24}]}
          testID={TEST_IDS.birthDateMessageTestID}
        >
          {_m('Players must be at least 18 years of age to compete on our system.')}
        </Text>
        <View style={styles.rowContainer}>
          {monthPrompt()}
          {datePrompt()}
          {yearPrompt()}
        </View>
        {errorMessageView()}
      </View>
    );
  };

  const getConsentTopMargin = () => {
    if (Sizes.isTablet()) {
      return errorText() ? 39 : 59;
    }
    return errorText() ? 34 : 54;
  };

  const renderEmailConsent = () => {
    return (
      <View style={[styles.textContainer, {marginTop: getConsentTopMargin()}]}>
        <View style={styles.rowContainer}>
          <Text
            allowFontScaling={false}
            style={styles.headerText}
            testID={TEST_IDS.stayInLoopHeaderTestID}
          >
            {_m('Stay in the loop?')}
          </Text>
          <View testID={isConsent ? TEST_IDS.emailConsentToggleTestID : ''}>
            <Toggle
              value={isConsent}
              onPress={toggleSwitch}
              thumbActiveComponent={
                <View testID={TEST_IDS.emailConsentToggleTestID}>
                  <SkillzSVG source={'check_mark.svg'} style={styles.checkIcon} />
                </View>
              }
              trackBarStyle={{
                borderColor: isConsent ? '#4890E8' : '#999999',
              }}
              trackBar={{
                activeBackgroundColor: '#4890E8',
                inActiveBackgroundColor: '#999999',
                width: Sizes.isTablet() ? 57 : 48,
                height: Sizes.isTablet() ? 31 : 26,
                borderWidth: 2,
              }}
              thumbButton={{
                width: Sizes.isTablet() ? 30 : 25,
                height: Sizes.isTablet() ? 30 : 25,
                activeBackgroundColor: '#4890E8',
                inActiveBackgroundColor: '#FFFFFF',
              }}
            />
          </View>
        </View>
        <Text
          allowFontScaling={false}
          style={styles.descriptionText}
          testID={TEST_IDS.stayInLoopMessageTestID}
        >
          {_m('Skillz can reach out to me about promotions, offers, and more.')}
        </Text>
      </View>
    );
  };

  const handleNextPress = (isAutoSubmit: boolean = false) => {
    if (isLoading) return; // Prevent multiple calls while loading

    const isError = onDateChange();
    if (month && date && year && !isError) {
      setIsLoading(true);
      const dateToSubmit = `${year}-${month}-${date}`;

      const onSuccess = () => {
        trackTelemetryEvent({
          event: TelemetryEventName.AGE_INPUT_SCREEN_CTA_CLICK,
          payload: {
            cta: isAutoSubmit ? 'auto_submit' : 'next',
            dob_date: date,
            dob_month: month,
            dob_year: year,
            success: true,
            ...(ClientPropertyConstants.isFTUEv3Enabled() && {
              cohort: getCohort(),
            }),
          },
        });
        setIsLoading(false);
      };

      const onError = () => {
        trackTelemetryEvent({
          event: TelemetryEventName.AGE_INPUT_SCREEN_CTA_CLICK,
          payload: {
            cta: isAutoSubmit ? 'auto_submit' : 'next',
            dob_date: date,
            dob_month: month,
            dob_year: year,
            success: false,
            ...(ClientPropertyConstants.isFTUEv3Enabled() && {
              cohort: getCohort(),
            }),
          },
        });
        setIsLoading(false);
      };
      props.onNextPress(dateToSubmit, isConsent, onSuccess, onError);
    }
  };

  const handleManualNextPress = () => {
    handleNextPress(false); // false = manual button click
  };

  const renderTermsOfServiceText = () => {
    const termsOfServiceLink = 'https://skillz.com/legal';
    const privacyPolicyLink = 'https://skillz.com/privacy-policy';
    if (isFTUEv3Enabled) {
      return (
        <View style={styles.newFooter} testID={TEST_IDS.serviceTermMessageTestID}>
          <Text allowFontScaling={false} style={[styles.newFooterTextStyle, style.footerTextColor]}>
            {_m('By tapping ‘Next’ I agree to ')}{' '}
            <Text
              onPress={() => {
                Linking.openURL(termsOfServiceLink);
              }}
              style={{textDecorationLine: 'underline'}}
            >
              terms of service
            </Text>
            <Text> and </Text>
            <Text
              onPress={() => {
                Linking.openURL(privacyPolicyLink);
              }}
              style={{textDecorationLine: 'underline'}}
            >
              privacy policy
            </Text>
          </Text>
        </View>
      );
    }
    return (
      <View style={styles.footer} testID={TEST_IDS.serviceTermMessageTestID}>
        <Text allowFontScaling={false} style={styles.footerTextStyle}>
          By tapping 'Next' I agree to the{' '}
        </Text>
        <TouchableOpacity
          testID={TEST_IDS.serviceTermButtonTestID}
          onPress={() => {
            Linking.openURL(termsOfServiceLink);
          }}
        >
          <Text allowFontScaling={false} style={styles.hyperlinkStyle}>
            Terms of Service
          </Text>
        </TouchableOpacity>
        <Text allowFontScaling={false} style={styles.footerTextStyle}>
          {' '}
          and{' '}
        </Text>
        <TouchableOpacity
          testID={TEST_IDS.privatePolicyButtonTestID}
          onPress={() => {
            Linking.openURL(privacyPolicyLink);
          }}
        >
          <Text allowFontScaling={false} style={styles.hyperlinkStyle}>
            Privacy Policy
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const isAllDateFieldsProvided = () => {
    return month.trim() !== '' && date.trim() !== '' && year.trim() !== '';
  };

  const renderFooter = () => {
    const isDisabled = !isAllDateFieldsProvided() || isLoading;

    return (
      <View>
        {isFTUEv3Enabled ? (
          <PushButton
            colors={SkillzStyle.getOverriddenFallbackTheme().playButtonGradient}
            cornerRadius={12}
            style={isDisabled ? style.nextButtonDisabled : style.nextButtonStyle}
            title={_m('Next')}
            textStyle={style.nextButtonTextStyle}
            onPress={handleNextPress}
            disabled={isDisabled}
            isLoading={isLoading}
          />
        ) : (
          <TouchableOpacity
            onPress={handleManualNextPress}
            testID={TEST_IDS.next}
            style={styles.nextBtn}
          >
            <Text allowFontScaling={false} style={styles.nextText}>
              {_m('Next')}
            </Text>
          </TouchableOpacity>
        )}

        {renderTermsOfServiceText()}
      </View>
    );
  };

  return (
    <>
      <KeyboardAvoidingView
        style={{flex: 1, ...(!isFTUEv3Enabled && {backgroundColor: 'white'})}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={20}
      >
        {isFTUEv3Enabled && <GameThemeBackground />}
        <ScrollView
          contentContainerStyle={[styles.container, style.containerBackground]}
          testID={TEST_IDS.modalTestID}
          showsVerticalScrollIndicator={false}
        >
          {renderNavBar()}
          <View
            style={{
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <View style={{height: isFTUEv3Enabled ? '78%' : '70%'}}>{renderDateOfBirth()}</View>
            {renderFooter()}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
};

export default AgeVerificationView;
